import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {customersApi} from '../lib/api-resources';
import type {Customer, CustomerCreate, CustomerUpdate, ListResponse} from '../lib/types';
import {ApiError} from '../lib/api';
import Modal from '../components/Modal';
import Confirm from '../components/Confirm';

const STATUS_OPTIONS: Array<{value: '' | 'ACTIVE' | 'INACTIVE'; label: string}> = [
  {value: '', label: '全部'},
  {value: 'ACTIVE', label: '启用'},
  {value: 'INACTIVE', label: '停用'},
];

export default function CustomersPage() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState<ListResponse<Customer> | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'ACTIVE' | 'INACTIVE'>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Customer | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Customer | null>(null);
  const [busy, setBusy] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState<Customer | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await customersApi.list({
        page,
        page_size: pageSize,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setList(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  async function handleSave(payload: CustomerCreate | CustomerUpdate) {
    setBusy(true);
    try {
      if (editing) {
        await customersApi.update(editing.id, payload as CustomerUpdate);
      } else {
        const created = await customersApi.create(payload as CustomerCreate);
        setCreatedCustomer(created);
      }
      setEditing(null);
      setCreating(false);
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    setBusy(true);
    try {
      await customersApi.delete(deleting.id);
      setDeleting(null);
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{t('customers.title')}</h1>
          <p className="page-subtitle">
            {list ? t('customers.countLabel', {count: list.total}) : t('common.loading')}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          {t('customers.create')}
        </button>
      </header>

      <div className="toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder={t('customers.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="btn btn-secondary">{t('common.search')}</button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as '' | 'ACTIVE' | 'INACTIVE');
            setPage(1);
          }}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {createdCustomer && (
        <div className="alert alert-success">
          客户已创建。下一步可以直接创建网站。
          <button
            className="btn-link primary"
            onClick={() => navigate(`/sites?customer_id=${encodeURIComponent(createdCustomer.id)}&create=1`)}
          >
            创建网站
          </button>
          <button className="btn-link" onClick={() => setCreatedCustomer(null)}>稍后</button>
        </div>
      )}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('customers.table.company')}</th>
              <th>{t('customers.table.contact')}</th>
              <th>{t('customers.table.email')}</th>
              <th>{t('customers.table.industry')}</th>
              <th>{t('customers.table.status')}</th>
              <th className="num">{t('customers.table.sites')}</th>
              <th className="num">{t('customers.table.servers')}</th>
              <th>{t('customers.table.created')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="muted center">{t('common.loading')}</td>
              </tr>
            )}
            {!loading && list && list.items.length === 0 && (
              <tr>
                <td colSpan={9} className="muted center">{t('customers.empty')}</td>
              </tr>
            )}
            {list?.items.map((c) => (
              <tr key={c.id}>
                <td>
                  <div className="cell-strong">{c.company_name}</div>
                  {c.country && <div className="cell-sub">{c.country}</div>}
                </td>
                <td>{c.contact_name || '—'}</td>
                <td className="mono small">{c.email || '—'}</td>
                <td>{c.industry || '—'}</td>
                <td>
                  <span className={`badge ${c.status === 'ACTIVE' ? 'ok' : 'muted'}`}>
                    {c.status}
                  </span>
                </td>
                <td className="num">{c.site_count}</td>
                <td className="num">{c.server_count}</td>
                <td className="muted small">{c.created_at.slice(0, 10)}</td>
                <td className="actions">
                  <button className="btn-link" onClick={() => setEditing(c)}>
                    {t('common.edit')}
                  </button>
                  <button
                    className="btn-link primary"
                    onClick={() => navigate(`/sites?customer_id=${encodeURIComponent(c.id)}&create=1`)}
                  >
                    创建网站
                  </button>
                  <button className="btn-link danger" onClick={() => setDeleting(c)}>
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {list && list.total_pages > 1 && (
        <div className="pager">
          <button
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← {t('common.previous')}
          </button>
          <span>
            {t('common.pageOf', {page, total: list.total_pages})}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= list.total_pages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common.next')} →
          </button>
        </div>
      )}

      {(creating || editing) && (
        <CustomerFormModal
          customer={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={handleSave}
          busy={busy}
        />
      )}

      {deleting && (
        <Confirm
          open
          title={t('customers.deleteConfirm.title')}
          message={
            <>
              {t('customers.deleteConfirm.message', {name: deleting.company_name})}
              <br />
              <span className="muted small">
                {t('customers.deleteConfirm.hint')}
              </span>
            </>
          }
          confirmText={t('common.delete')}
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

function CustomerFormModal({
  customer,
  onClose,
  onSave,
  busy,
}: {
  customer: Customer | null;
  onClose: () => void;
  onSave: (p: CustomerCreate | CustomerUpdate) => Promise<void>;
  busy: boolean;
}) {
  const {t} = useTranslation();
  const [form, setForm] = useState({
    company_name: customer?.company_name ?? '',
    contact_name: customer?.contact_name ?? '',
    phone: customer?.phone ?? '',
    email: customer?.email ?? '',
    country: customer?.country ?? '',
    industry: customer?.industry ?? '',
    notes: customer?.notes ?? '',
    status: (customer?.status ?? 'ACTIVE') as 'ACTIVE' | 'INACTIVE',
  });

  function update<K extends keyof typeof form>(key: K, v: (typeof form)[K]) {
    setForm((f) => ({...f, [key]: v}));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const payload: CustomerCreate = {
      company_name: form.company_name,
      contact_name: form.contact_name || null,
      phone: form.phone || null,
      email: form.email || null,
      country: form.country || null,
      industry: form.industry || null,
      notes: form.notes || null,
      status: form.status,
    };
    await onSave(payload);
  }

  return (
    <Modal
      open
      title={customer ? t('customers.modal.edit', {name: customer.company_name}) : t('customers.modal.new')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            form="customer-form"
            type="submit"
            disabled={busy || !form.company_name.trim()}
          >
            {busy ? t('common.saving') : t('common.save')}
          </button>
        </>
      }
    >
      <form id="customer-form" onSubmit={submit} className="form-grid">
        <label className="full">
          <span>{t('customers.modal.companyName')} *</span>
          <input
            value={form.company_name}
            onChange={(e) => update('company_name', e.target.value)}
            required
            maxLength={200}
          />
        </label>
        <label>
          <span>{t('customers.modal.contactName')}</span>
          <input
            value={form.contact_name}
            onChange={(e) => update('contact_name', e.target.value)}
            maxLength={100}
          />
        </label>
        <label>
          <span>{t('common.status')}</span>
          <select
            value={form.status}
            onChange={(e) => update('status', e.target.value as 'ACTIVE' | 'INACTIVE')}
          >
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
          </select>
        </label>
        <label>
          <span>{t('customers.modal.phone')}</span>
          <input
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
            maxLength={50}
          />
        </label>
        <label>
          <span>{t('common.email')}</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
          />
        </label>
        <label>
          <span>{t('customers.modal.country')}</span>
          <input
            value={form.country}
            onChange={(e) => update('country', e.target.value)}
            maxLength={100}
          />
        </label>
        <label>
          <span>{t('customers.modal.industryLabel')}</span>
          <input
            value={form.industry}
            onChange={(e) => update('industry', e.target.value)}
            maxLength={100}
          />
        </label>
        <label className="full">
          <span>{t('customers.modal.notes')}</span>
          <textarea
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
            maxLength={2000}
            rows={3}
          />
        </label>
      </form>
    </Modal>
  );
}
